import { MOTION } from '@/utils';
import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';
import { useCallback, useEffect, useRef, useState } from 'react';

const VIMEO_SRC =
  'https://player.vimeo.com/video/1195888283?badge=0&autoplay=1&muted=1&loop=1&controls=0&player_id=0&app_id=58479';

const formatTime = (s: number) => {
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
};

const MINI_WIDTH = 256;
const MINI_HEIGHT = 144;
const MINI_RIGHT = 24;
const MINI_BOTTOM = 24;
const MINI_RADIUS = 8;
const EXPANDED_RADIUS = 12;

const getMiniPos = () => ({
  left: Math.round(window.innerWidth - MINI_WIDTH - MINI_RIGHT),
  top: Math.round(window.innerHeight - MINI_HEIGHT - MINI_BOTTOM),
});

export function VideoWidget() {
  const [isReady, setIsReady] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isBackdropVisible, setIsBackdropVisible] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const widgetRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<globalThis.HTMLIFrameElement>(null);
  const miniControlsRef = useRef<HTMLDivElement>(null);
  const isSeekingRef = useRef(false);
  const durationFetchedRef = useRef(false);
  const expandedControlsRef = useRef<HTMLDivElement>(null);
  const tl = useRef<gsap.core.Timeline | null>(null);

  useGSAP(() => {
    if (!widgetRef.current || !iframeRef.current) return;
    gsap.set(widgetRef.current, {
      ...getMiniPos(),
      width: MINI_WIDTH,
      height: MINI_HEIGHT,
      borderRadius: MINI_RADIUS,
      overflow: 'hidden',
      transformOrigin: 'bottom right',
      autoAlpha: 0,
    });
    gsap.set(iframeRef.current, { width: MINI_WIDTH, height: MINI_HEIGHT });
  }, []);

  // Track Vimeo player state via postMessage
  useEffect(() => {
    const onMessage = (e: globalThis.MessageEvent) => {
      if (e.origin !== 'https://player.vimeo.com') return;
      if (typeof e.data !== 'string') return;
      try {
        const data = JSON.parse(e.data) as { event?: string; method?: string; value?: unknown };
        if (data.event === 'ready') {
          iframeRef.current?.contentWindow?.postMessage(
            JSON.stringify({ method: 'addEventListener', value: 'play' }),
            'https://player.vimeo.com',
          );
          iframeRef.current?.contentWindow?.postMessage(
            JSON.stringify({ method: 'addEventListener', value: 'pause' }),
            'https://player.vimeo.com',
          );
          iframeRef.current?.contentWindow?.postMessage(
            JSON.stringify({ method: 'addEventListener', value: 'volumechange' }),
            'https://player.vimeo.com',
          );
        }
        if (data.event === 'play') {
          setIsPlaying(true);
          setIsReady(true);
          if (!durationFetchedRef.current) {
            durationFetchedRef.current = true;
            iframeRef.current?.contentWindow?.postMessage(
              JSON.stringify({ method: 'getDuration' }),
              'https://player.vimeo.com',
            );
          }
        }
        if (data.event === 'pause') setIsPlaying(false);
        if (data.event === 'volumechange') {
          const d = data as { event?: string; data?: { volume: number } };
          setIsMuted((d.data?.volume ?? 1) === 0);
        }
        if (data.method === 'getDuration') setDuration(data.value as number);
        if (data.method === 'getCurrentTime' && !isSeekingRef.current)
          setCurrentTime(data.value as number);
      } catch {
        // non-JSON message, ignore
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // Entrance animation: scale from bottom-right, triggered on first play
  useGSAP(() => {
    if (!isReady || !widgetRef.current) return;
    gsap.fromTo(
      widgetRef.current,
      { autoAlpha: 0, scale: 0.5 },
      { autoAlpha: 1, scale: 1, duration: MOTION.durationMd, ease: MOTION.ease },
    );
  }, [isReady]);

  // Keep widget in corner on resize
  useEffect(() => {
    const onResize = () => {
      if (isExpanded || !widgetRef.current) return;
      gsap.set(widgetRef.current, getMiniPos());
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [isExpanded]);

  const postToVimeo = useCallback((method: string) => {
    if (!iframeRef.current) return;
    iframeRef.current.contentWindow?.postMessage(
      JSON.stringify({ method }),
      'https://player.vimeo.com',
    );
  }, []);

  const expand = useCallback(() => {
    if (!widgetRef.current) return;
    tl.current?.kill();
    setIsBackdropVisible(true);
    document.body.style.overflow = 'hidden';

    if (miniControlsRef.current) gsap.set(miniControlsRef.current, { autoAlpha: 0 });
    gsap.set(widgetRef.current, { zIndex: 300 });

    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const w = Math.round(vw * 0.9);
    const h = Math.round(w * (9 / 16));

    tl.current = gsap.timeline({
      onComplete: () => {
        if (!widgetRef.current) return;
        setIsExpanded(true);
      },
    });
    tl.current
      .to(widgetRef.current, {
        left: Math.round((vw - w) / 2),
        top: Math.round((vh - h) / 2),
        width: w,
        height: h,
        borderRadius: EXPANDED_RADIUS,
        duration: 0.6,
        ease: 'expo.inOut',
      })
      .to(iframeRef.current, { width: w, height: h, duration: 0.6, ease: 'expo.inOut' }, '<');
  }, []);

  const collapse = useCallback(() => {
    if (!widgetRef.current) return;
    tl.current?.kill();
    if (expandedControlsRef.current) gsap.set(expandedControlsRef.current, { autoAlpha: 0 });
    document.body.style.overflow = '';
    setIsBackdropVisible(false);

    tl.current = gsap.timeline({
      onComplete: () => {
        if (!widgetRef.current) return;
        gsap.set(widgetRef.current, { clearProps: 'zIndex' });
        setIsExpanded(false);
      },
    });
    tl.current
      .to(widgetRef.current, {
        ...getMiniPos(),
        width: MINI_WIDTH,
        height: MINI_HEIGHT,
        borderRadius: MINI_RADIUS,
        duration: 0.6,
        ease: 'expo.inOut',
      })
      .to(
        iframeRef.current,
        { width: MINI_WIDTH, height: MINI_HEIGHT, duration: 0.6, ease: 'expo.inOut' },
        '<',
      );
  }, []);

  const togglePlayPause = useCallback(() => {
    postToVimeo(isPlaying ? 'pause' : 'play');
  }, [isPlaying, postToVimeo]);

  const seek = useCallback((time: number) => {
    if (!iframeRef.current) return;
    iframeRef.current.contentWindow?.postMessage(
      JSON.stringify({ method: 'setCurrentTime', value: time }),
      'https://player.vimeo.com',
    );
  }, []);

  const toggleMute = useCallback(() => {
    if (!iframeRef.current) return;
    iframeRef.current.contentWindow?.postMessage(
      JSON.stringify({ method: 'setVolume', value: isMuted ? 1 : 0 }),
      'https://player.vimeo.com',
    );
  }, [isMuted]);

  useEffect(() => {
    if (!isExpanded) return;
    const onKeyDown = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') collapse();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isExpanded, collapse]);

  useEffect(() => {
    if (!isExpanded || !isPlaying) return;
    const id = setInterval(() => {
      iframeRef.current?.contentWindow?.postMessage(
        JSON.stringify({ method: 'getCurrentTime' }),
        'https://player.vimeo.com',
      );
    }, 250);
    return () => clearInterval(id);
  }, [isExpanded, isPlaying]);

  return (
    <>
      <div
        className={[
          'fixed inset-0 z-[299] bg-black/80 transition-opacity duration-300',
          isBackdropVisible ? 'opacity-100' : 'pointer-events-none opacity-0',
        ].join(' ')}
        onClick={collapse}
      />

      <div
        ref={widgetRef}
        className="group fixed z-200 hidden shadow-2xl md:block"
        style={{ visibility: 'hidden' }}
      >
        <iframe
          ref={iframeRef}
          allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
          className="block border-0"
          referrerPolicy="strict-origin-when-cross-origin"
          src={VIMEO_SRC}
          title="Et si vous gagniez 30 % de chiffre d'affaires en plus ?"
          allowFullScreen
        />

        {!isExpanded && (
          <div ref={miniControlsRef}>
            <div
              aria-haspopup="dialog"
              aria-label="Agrandir la vidéo"
              className="absolute inset-0 z-[1] cursor-pointer"
              role="button"
              tabIndex={-1}
              onClick={expand}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') expand();
              }}
            />
            <div className="absolute right-0 bottom-0 left-0 z-[2] flex items-center justify-between gap-1 bg-gradient-to-t from-black/70 to-transparent px-2 py-1.5">
              <button
                aria-label={isPlaying ? 'Mettre en pause' : 'Reprendre la lecture'}
                className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-full text-white transition-opacity duration-200 hover:opacity-80"
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  togglePlayPause();
                }}
              >
                {isPlaying ? (
                  <svg
                    aria-hidden="true"
                    className="h-3 w-3"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                  </svg>
                ) : (
                  <svg
                    aria-hidden="true"
                    className="h-3 w-3 translate-x-px"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M8 5v14l11-7z" />
                  </svg>
                )}
              </button>

              <button
                aria-label="Agrandir la vidéo"
                className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-full text-white transition-opacity duration-200 hover:opacity-80"
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  expand();
                }}
              >
                <svg
                  aria-hidden="true"
                  className="h-3 w-3"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              <button
                aria-label={isMuted ? 'Activer le son' : 'Couper le son'}
                className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-full text-white transition-opacity duration-200 hover:opacity-80"
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleMute();
                }}
              >
                {isMuted ? (
                  <svg
                    aria-hidden="true"
                    className="h-3 w-3"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M3.63 3.63a.996.996 0 000 1.41L7.29 8.7 7 9H4c-.55 0-1 .45-1 1v4c0 .55.45 1 1 1h3l3.29 3.29c.63.63 1.71.18 1.71-.71v-4.17l4.18 4.18a6.957 6.957 0 01-2.18.77.99.99 0 00-.82 1.16c.11.59.71.96 1.29.82a9.02 9.02 0 003.32-1.43l1.59 1.59a.996.996 0 101.41-1.41L5.05 3.63a.996.996 0 00-1.42 0zM19 12c0 .82-.15 1.61-.41 2.34l1.53 1.53c.56-1.17.88-2.48.88-3.87 0-3.83-2.4-7.11-5.78-8.4-.59-.23-1.22.23-1.22.86v.19c0 .38.25.71.61.85C17.18 6.54 19 9.06 19 12zm-8.71-6.29l-.17.17L12 7.76V6.41c0-.89-1.08-1.33-1.71-.7zM16.5 12A4.5 4.5 0 0014 7.97v1.79l2.48 2.48c.01-.08.02-.16.02-.24z" />
                  </svg>
                ) : (
                  <svg
                    aria-hidden="true"
                    className="h-3 w-3"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        )}

        {isExpanded && (
          <div ref={expandedControlsRef}>
            <button
              aria-label="Réduire la vidéo"
              className="text-orange absolute top-4 right-4 z-10 inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-white shadow-md transition-transform duration-200 hover:scale-105"
              type="button"
              onClick={collapse}
            >
              <svg aria-hidden="true" className="h-4 w-4" fill="none" viewBox="0 0 24 24">
                <path
                  d="M8 3v3a2 2 0 01-2 2H3m18 0h-3a2 2 0 01-2-2V3m0 18v-3a2 2 0 012-2h3M3 16h3a2 2 0 012 2v3"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                />
              </svg>
            </button>

            <div className="absolute bottom-0 left-0 right-0 z-10 flex flex-col gap-2 bg-gradient-to-t from-black/70 to-transparent px-4 pb-4 pt-8">
              <div className="flex items-center gap-3">
                <span className="min-w-[36px] text-right text-xs text-white/80 tabular-nums">
                  {formatTime(currentTime)}
                </span>
                <input
                  aria-label="Avancer dans la vidéo"
                  className="h-1 flex-1 cursor-pointer accent-white"
                  max={duration || 1}
                  min={0}
                  step={0.1}
                  type="range"
                  value={currentTime}
                  onChange={(e) => setCurrentTime(Number(e.target.value))}
                  onPointerDown={() => { isSeekingRef.current = true; }}
                  onPointerUp={(e) => {
                    const seekTime = Number(e.currentTarget.value);
                    setCurrentTime(seekTime);
                    seek(seekTime);
                    setTimeout(() => { isSeekingRef.current = false; }, 300);
                  }}
                />
                <span className="min-w-[36px] text-xs text-white/80 tabular-nums">
                  {formatTime(duration)}
                </span>
              </div>

              <div className="flex items-center gap-3">
                <button
                  aria-label={isPlaying ? 'Mettre en pause' : 'Reprendre la lecture'}
                  className="flex h-8 w-8 cursor-pointer items-center justify-center text-white transition-opacity duration-200 hover:opacity-80"
                  type="button"
                  onClick={togglePlayPause}
                >
                  {isPlaying ? (
                    <svg aria-hidden="true" className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                    </svg>
                  ) : (
                    <svg aria-hidden="true" className="h-4 w-4 translate-x-px" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  )}
                </button>

                <button
                  aria-label={isMuted ? 'Activer le son' : 'Couper le son'}
                  className="flex h-8 w-8 cursor-pointer items-center justify-center text-white transition-opacity duration-200 hover:opacity-80"
                  type="button"
                  onClick={toggleMute}
                >
                  {isMuted ? (
                    <svg aria-hidden="true" className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M3.63 3.63a.996.996 0 000 1.41L7.29 8.7 7 9H4c-.55 0-1 .45-1 1v4c0 .55.45 1 1 1h3l3.29 3.29c.63.63 1.71.18 1.71-.71v-4.17l4.18 4.18a6.957 6.957 0 01-2.18.77.99.99 0 00-.82 1.16c.11.59.71.96 1.29.82a9.02 9.02 0 003.32-1.43l1.59 1.59a.996.996 0 101.41-1.41L5.05 3.63a.996.996 0 00-1.42 0zM19 12c0 .82-.15 1.61-.41 2.34l1.53 1.53c.56-1.17.88-2.48.88-3.87 0-3.83-2.4-7.11-5.78-8.4-.59-.23-1.22.23-1.22.86v.19c0 .38.25.71.61.85C17.18 6.54 19 9.06 19 12zm-8.71-6.29l-.17.17L12 7.76V6.41c0-.89-1.08-1.33-1.71-.7zM16.5 12A4.5 4.5 0 0014 7.97v1.79l2.48 2.48c.01-.08.02-.16.02-.24z" />
                    </svg>
                  ) : (
                    <svg aria-hidden="true" className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
